import type { HaloCaches } from '@halo-data/cache';
import { compareXuids, wrapXuid } from '@halo-data/utilities';
import { MatchInfo, MatchSkill, ResultContainer } from 'halo-infinite-api';
import { Observer } from 'rxjs';
import type { ILeaderboardProvider } from './leaderboard-provider';
import { queueLeaderboardEntryForProcessing } from './leaderboard-entry-queue';
import { PlayerMatchHistoryStatsSkill } from './player-match-history-stats-skill';

export async function fetchFullyLoadedMatch(
  leaderboard:
    | Pick<ILeaderboardProvider, 'addLeaderboardEntries' | 'containsXuid'>
    | undefined,
  match: { MatchId: string; MatchInfo: MatchInfo },
  users: { xuid: string }[],
  signal: AbortSignal,
  haloCaches: HaloCaches,
  loadUserData: boolean,
  _logger$?: Observer<string>
): Promise<PlayerMatchHistoryStatsSkill> {
  const matchStatsPromise = haloCaches.matchStatsCache.get(match.MatchId); // This is causing abort errors for some reason

  const [
    stats,
    skills,
    MapVariant,
    UgcGameVariant,
    playlist,
    playlistVersion,
    userInfoMap,
  ] = await Promise.all([
    matchStatsPromise,
    matchStatsPromise.then((matchStats) => {
      const playersToFetch = matchStats.Players.filter((p) =>
        /^xuid\(\d+\)$/.test(p.PlayerId)
      ).map((p) => ({
        matchId: match.MatchId,
        playerId: p.PlayerId,
      }));
      return Promise.all(
        haloCaches.matchSkillsCache.get(playersToFetch, signal).map((p, i) =>
          p.catch(() => ({
            Id: playersToFetch[i].playerId,
            ResultCode: 1,
            Result: null,
          }))
        )
      );
    }),
    haloCaches.mapCache.get(match.MatchInfo.MapVariant, signal),
    haloCaches.gameVariantCache.get(match.MatchInfo.UgcGameVariant, signal),
    match.MatchInfo.Playlist?.AssetId
      ? haloCaches.playlistCache
          .get(match.MatchInfo.Playlist.AssetId)
          .catch(() => null)
      : null,
    match.MatchInfo.Playlist
      ? haloCaches.playlistVersionCache.get(match.MatchInfo.Playlist, signal)
      : null,
    loadUserData
      ? matchStatsPromise.then((s) =>
          haloCaches.usersCache.get(
            s.Players.filter((p) => /^xuid\(\d+\)$/.test(p.PlayerId)).map((p) =>
              wrapXuid(p.PlayerId)
            ),
            signal
          )
        )
      : new Map<
          string,
          Promise<{
            xuid: string;
            gamertag: string;
          }>
        >(),
  ]);

  if (match.MatchInfo.Playlist?.AssetId && leaderboard) {
    const playlistAssetId = match.MatchInfo.Playlist.AssetId;
    queueLeaderboardEntryForProcessing(
      haloCaches,
      leaderboard,
      skills
        .filter(
          (s): s is ResultContainer<MatchSkill> =>
            s.Result != null && s.ResultCode === 0
        )
        .map((s) => ({
          matchInfo: {
            startTime: match.MatchInfo.StartTime,
            playlistAssetId,
            gameVariantAssetId: match.MatchInfo.UgcGameVariant?.AssetId ?? '',
            matchId: match.MatchId,
          },
          matchSkill: s.Result,
          xuid: s.Id,
        }))
    );
  }

  const Players = await Promise.all(
    stats.Players.map(async (p) => {
      const userInfo = (await userInfoMap.get(p.PlayerId)) ?? {
        xuid: p.PlayerId,
        gamertag: '',
      };
      const playerSkill = skills?.find(
        (s) => s.ResultCode === 0 && s.Result != null && s.Id === p.PlayerId
      )?.Result;
      if (playerSkill) {
        return { ...p, Skill: playerSkill, ...userInfo };
      } else {
        return { ...p, ...userInfo };
      }
    })
  );

  return {
    ...match,
    MatchInfo: {
      ...match.MatchInfo,
      MapVariant,
      UgcGameVariant,
      Playlist:
        playlist && playlistVersion
          ? {
              ...playlist,
              ...playlistVersion,
            }
          : match.MatchInfo.Playlist,
    },
    Players: await Promise.all(
      (users.length
        ? stats.Players.filter((p) =>
            users.some((u) => compareXuids(u.xuid, p.PlayerId))
          )
        : stats.Players
      ).map(
        async (player) =>
          (await userInfoMap.get(player.PlayerId)) ?? {
            xuid: player.PlayerId,
            gamertag: '',
          }
      )
    ),
    MatchStats: {
      ...stats,
      Players,
      Teams: stats.Teams.map((t) => ({
        ...t,
        Players: Players.filter((p) => p.LastTeamId === t.TeamId),
      })),
    },
  } satisfies PlayerMatchHistoryStatsSkill;
}
